import { Component, input, output, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';

/**
 * Wizard Step
 */
export interface WizardStep {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  optional?: boolean;
  valid?: boolean;
  completed?: boolean;
}

/**
 * Wizard Component
 * 
 * Multi-step form wizard with navigation, validation, and progress tracking.
 * Identified as critical UX improvement in the analysis.
 * 
 * @example
 * ```typescript
 * // In your component
 * steps: WizardStep[] = [
 *   { id: 'info', title: 'Informações Básicas', icon: '📝' },
 *   { id: 'details', title: 'Detalhes', icon: '📋' },
 *   { id: 'review', title: 'Revisão', icon: '✓', optional: true }
 * ];
 * 
 * currentStep = signal(0);
 * ```
 * 
 * ```html
 * <mg3p-wizard
 *   [steps]="steps"
 *   [currentStep]="currentStep()"
 *   (currentStepChange)="currentStep.set($event)"
 *   (complete)="onComplete()"
 *   (cancel)="onCancel()"
 * >
 *   <div step-content="info">
 *     <!-- Step 1 content -->
 *   </div>
 *   <div step-content="details">
 *     <!-- Step 2 content -->
 *   </div>
 *   <div step-content="review">
 *     <!-- Step 3 content -->
 *   </div>
 * </mg3p-wizard>
 * ```
 * 
 * Features:
 * - Linear and non-linear navigation
 * - Step validation
 * - Progress indicator
 * - Optional steps
 * - Save draft functionality
 * - Keyboard navigation
 * - Mobile responsive
 * - WCAG 2.1 Level AA compliant
 */
@Component({
  selector: 'mg3p-wizard',
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  template: `
    <div class="wizard" [class.wizard--mobile]="isMobile()">
      <!-- Header -->
      <div class="wizard__header">
        <h2 class="wizard__title">{{ title() }}</h2>
        @if (description()) {
          <p class="wizard__description">{{ description() }}</p>
        }
      </div>

      <!-- Progress bar -->
      <div class="wizard__progress">
        <div 
          class="wizard__progress-bar"
          [style.width.%]="progressPercentage()"
          role="progressbar"
          [attr.aria-valuenow]="progressPercentage()"
          [attr.aria-valuemin]="0"
          [attr.aria-valuemax]="100"
          [attr.aria-label]="'Progresso: ' + progressPercentage() + '%'"
        ></div>
      </div>

      <!-- Steps indicator -->
      <nav class="wizard__steps" [attr.aria-label]="'Etapas do formulário'">
        <ol class="wizard__steps-list">
          @for (step of steps(); track step.id; let index = $index) {
            <li 
              class="wizard__step"
              [class.wizard__step--active]="index === currentStep()"
              [class.wizard__step--completed]="isStepCompleted(index)"
              [class.wizard__step--error]="isStepInvalid(index)"
            >
              <button
                type="button"
                class="wizard__step-button"
                [disabled]="!canNavigateToStep(index)"
                (click)="goToStep(index)"
                [attr.aria-current]="index === currentStep() ? 'step' : null"
              >
                <!-- Step number/icon -->
                <span class="wizard__step-indicator" aria-hidden="true">
                  @if (isStepCompleted(index)) {
                    <span class="wizard__step-check">✓</span>
                  } @else if (step.icon) {
                    <span class="wizard__step-icon">{{ step.icon }}</span>
                  } @else {
                    <span class="wizard__step-number">{{ index + 1 }}</span>
                  }
                </span>

                <!-- Step info -->
                <span class="wizard__step-info">
                  <span class="wizard__step-title">
                    {{ step.title }}
                    @if (step.optional) {
                      <span class="wizard__step-optional">(opcional)</span>
                    }
                  </span>
                  @if (step.description && !isMobile()) {
                    <span class="wizard__step-description">{{ step.description }}</span>
                  }
                </span>
              </button>

              <!-- Connector line -->
              @if (index < steps().length - 1) {
                <div class="wizard__step-connector" aria-hidden="true"></div>
              }
            </li>
          }
        </ol>
      </nav>

      <!-- Content -->
      <div class="wizard__content">
        <div class="wizard__content-inner">
          <!-- Current step info -->
          <div class="wizard__step-header">
            <h3 class="wizard__step-heading">
              {{ currentStepData().title }}
            </h3>
            @if (currentStepData().description) {
              <p class="wizard__step-desc">{{ currentStepData().description }}</p>
            }
          </div>

          <!-- Step content slot -->
          <div class="wizard__step-content">
            <ng-content [select]="'[step-content=' + currentStepData().id + ']'"></ng-content>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="wizard__actions">
        <div class="wizard__actions-left">
          @if (showCancelButton()) {
            <mg3p-button
              variant="ghost"
              (click)="onCancel()"
              [disabled]="loading()"
            >
              {{ cancelLabel() }}
            </mg3p-button>
          }

          @if (showSaveDraftButton()) {
            <mg3p-button
              variant="secondary"
              (click)="onSaveDraft()"
              [loading]="savingDraft()"
              [disabled]="loading()"
            >
              {{ saveDraftLabel() }}
            </mg3p-button>
          }
        </div>

        <div class="wizard__actions-right">
          @if (!isFirstStep()) {
            <mg3p-button
              variant="secondary"
              (click)="previousStep()"
              [disabled]="loading()"
            >
              {{ previousLabel() }}
            </mg3p-button>
          }

          @if (!isLastStep()) {
            <mg3p-button
              variant="primary"
              (click)="nextStep()"
              [disabled]="!canProceed() || loading()"
              [loading]="loading()"
            >
              {{ nextLabel() }}
            </mg3p-button>
          } @else {
            <mg3p-button
              variant="primary"
              (click)="onComplete()"
              [disabled]="!canComplete() || loading()"
              [loading]="loading()"
            >
              {{ completeLabel() }}
            </mg3p-button>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    .wizard {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xl);
      max-width: 1200px;
      margin: 0 auto;
    }

    /* Header */
    .wizard__header {
      text-align: center;
    }

    .wizard__title {
      font-size: var(--font-size-2xl);
      font-weight: var(--font-weight-bold);
      color: var(--color-text-primary);
      margin: 0 0 var(--spacing-sm) 0;
    }

    .wizard__description {
      font-size: var(--font-size-md);
      color: var(--color-text-secondary);
      margin: 0;
    }

    /* Progress bar */
    .wizard__progress {
      height: 4px;
      background: var(--color-surface-secondary);
      border-radius: var(--radius-full);
      overflow: hidden;
    }

    .wizard__progress-bar {
      height: 100%;
      background: var(--color-primary);
      transition: width var(--transition-normal);
    }

    /* Steps indicator */
    .wizard__steps {
      padding: var(--spacing-lg) 0;
    }

    .wizard__steps-list {
      display: flex;
      justify-content: space-between;
      list-style: none;
      margin: 0;
      padding: 0;
      position: relative;
    }

    .wizard__step {
      flex: 1;
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .wizard__step-button {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-sm);
      padding: var(--spacing-sm);
      background: transparent;
      border: none;
      cursor: pointer;
      transition: all var(--transition-fast);
      text-align: center;
      width: 100%;
    }

    .wizard__step-button:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .wizard__step-button:not(:disabled):hover {
      background: var(--color-surface-hover);
      border-radius: var(--radius-md);
    }

    .wizard__step-button:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
      border-radius: var(--radius-md);
    }

    .wizard__step-indicator {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: var(--radius-full);
      background: var(--color-surface-secondary);
      border: 2px solid var(--color-border);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-secondary);
      transition: all var(--transition-fast);
    }

    .wizard__step--active .wizard__step-indicator {
      background: var(--color-primary);
      border-color: var(--color-primary);
      color: white;
      box-shadow: 0 0 0 4px var(--color-primary-light);
    }

    .wizard__step--completed .wizard__step-indicator {
      background: var(--color-success);
      border-color: var(--color-success);
      color: white;
    }

    .wizard__step--error .wizard__step-indicator {
      background: var(--color-danger);
      border-color: var(--color-danger);
      color: white;
    }

    .wizard__step-check,
    .wizard__step-icon,
    .wizard__step-number {
      font-size: var(--font-size-lg);
      line-height: 1;
    }

    .wizard__step-info {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      max-width: 150px;
    }

    .wizard__step-title {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-secondary);
    }

    .wizard__step--active .wizard__step-title {
      color: var(--color-primary);
      font-weight: var(--font-weight-semibold);
    }

    .wizard__step--completed .wizard__step-title {
      color: var(--color-text-primary);
    }

    .wizard__step-optional {
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-normal);
      color: var(--color-text-tertiary);
      margin-left: var(--spacing-xs);
    }

    .wizard__step-description {
      font-size: var(--font-size-xs);
      color: var(--color-text-tertiary);
      line-height: 1.4;
    }

    .wizard__step-connector {
      position: absolute;
      top: 20px;
      left: 50%;
      right: -50%;
      height: 2px;
      background: var(--color-border);
      z-index: -1;
    }

    .wizard__step--completed .wizard__step-connector {
      background: var(--color-success);
    }

    .wizard__step:last-child .wizard__step-connector {
      display: none;
    }

    /* Content */
    .wizard__content {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-lg);
      padding: var(--spacing-xl);
      min-height: 400px;
      overflow: visible;
    }

    .wizard__content-inner {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-lg);
      overflow: visible;
    }

    .wizard__step-header {
      border-bottom: 1px solid var(--color-border);
      padding-bottom: var(--spacing-md);
    }

    .wizard__step-heading {
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      margin: 0 0 var(--spacing-xs) 0;
    }

    .wizard__step-desc {
      font-size: var(--font-size-md);
      color: var(--color-text-secondary);
      margin: 0;
    }

    .wizard__step-content {
      flex: 1;
      overflow: visible;
    }

    /* Actions */
    .wizard__actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--spacing-md);
      padding-top: var(--spacing-lg);
      border-top: 1px solid var(--color-border);
    }

    .wizard__actions-left,
    .wizard__actions-right {
      display: flex;
      gap: var(--spacing-sm);
    }

    /* Mobile */
    @media (max-width: 768px) {
      .wizard--mobile .wizard__steps-list {
        overflow-x: auto;
        padding-bottom: var(--spacing-sm);
      }

      .wizard--mobile .wizard__step {
        min-width: 100px;
      }

      .wizard--mobile .wizard__step-info {
        max-width: 80px;
      }

      .wizard--mobile .wizard__step-description {
        display: none;
      }

      .wizard--mobile .wizard__content {
        padding: var(--spacing-md);
      }

      .wizard--mobile .wizard__actions {
        flex-direction: column;
      }

      .wizard--mobile .wizard__actions-left,
      .wizard--mobile .wizard__actions-right {
        width: 100%;
        justify-content: stretch;
      }

      .wizard--mobile .wizard__actions-left mg3p-button,
      .wizard--mobile .wizard__actions-right mg3p-button {
        flex: 1;
      }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .wizard__progress-bar,
      .wizard__step-indicator,
      .wizard__step-button {
        transition: none;
      }
    }
  `]
})
export class WizardComponent {
  // Inputs
  title = input<string>('');
  description = input<string>('');
  steps = input.required<WizardStep[]>();
  currentStep = input<number>(0);
  linear = input<boolean>(true);
  showCancelButton = input<boolean>(true);
  showSaveDraftButton = input<boolean>(false);
  cancelLabel = input<string>('Cancelar');
  previousLabel = input<string>('Anterior');
  nextLabel = input<string>('Próximo');
  completeLabel = input<string>('Concluir');
  saveDraftLabel = input<string>('Salvar Rascunho');
  loading = input<boolean>(false);

  // Outputs
  currentStepChange = output<number>();
  complete = output<void>();
  cancel = output<void>();
  saveDraft = output<void>();
  stepChange = output<{ from: number; to: number }>();

  // State
  isMobile = signal<boolean>(false);
  savingDraft = signal<boolean>(false);

  // Computed
  currentStepData = computed(() => {
    return this.steps()[this.currentStep()];
  });

  progressPercentage = computed(() => {
    const total = this.steps().length;
    const current = this.currentStep() + 1;
    return Math.round((current / total) * 100);
  });

  canProceed = computed(() => {
    const step = this.currentStepData();
    return step?.valid !== false;
  });

  canComplete = computed(() => {
    const steps = this.steps();
    return steps.every(step => step.optional || step.valid !== false);
  });

  constructor() {
    // Detect mobile
    effect(() => {
      this.isMobile.set(window.innerWidth <= 768);
    });
  }

  isFirstStep(): boolean {
    return this.currentStep() === 0;
  }

  isLastStep(): boolean {
    return this.currentStep() === this.steps().length - 1;
  }

  isStepCompleted(index: number): boolean {
    return index < this.currentStep() || this.steps()[index].completed === true;
  }

  isStepInvalid(index: number): boolean {
    const step = this.steps()[index];
    return step.valid === false && !step.optional;
  }

  canNavigateToStep(index: number): boolean {
    if (!this.linear()) return true;
    if (index <= this.currentStep()) return true;
    
    // Check if all previous steps are valid
    for (let i = 0; i < index; i++) {
      const step = this.steps()[i];
      if (!step.optional && step.valid === false) {
        return false;
      }
    }
    
    return true;
  }

  goToStep(index: number): void {
    if (!this.canNavigateToStep(index)) return;
    
    const from = this.currentStep();
    this.currentStepChange.emit(index);
    this.stepChange.emit({ from, to: index });
  }

  nextStep(): void {
    if (!this.canProceed() || this.isLastStep()) return;
    this.goToStep(this.currentStep() + 1);
  }

  previousStep(): void {
    if (this.isFirstStep()) return;
    this.goToStep(this.currentStep() - 1);
  }

  onComplete(): void {
    if (!this.canComplete()) return;
    this.complete.emit();
  }

  onCancel(): void {
    this.cancel.emit();
  }

  async onSaveDraft(): Promise<void> {
    this.savingDraft.set(true);
    this.saveDraft.emit();
    // Reset after 2 seconds (parent should handle actual save)
    setTimeout(() => this.savingDraft.set(false), 2000);
  }
}

// Made with Bob
